import { useCallback } from "react";
import { useDispatch } from "react-redux";

import {
  generatedV2,
  useV2CreateProjectServiceMutation,
  V2GetServiceByIdApiResponse,
  V2ListProjectPhasesForAccountApiResponse,
  V2ListProjectResourcesForAccountApiResponse,
  V2ProjectServiceResource,
} from "@generated";
import { ServiceValues } from "@components/ServiceValuesModal";
import {
  projectPhasesArgs,
  projectResourcesArgs,
} from "@components/ServiceValuesModal/useServiceValueOptions";
import { apiErrorMessage } from "../../Projects/ServicesV2/common/helpers";

export type SuggestedServiceType = "professional_services" | "managed_services";

export interface SuggestedServiceDefaults {
  name: string;
  serviceType: SuggestedServiceType;
  values: ServiceValues;
}

const SERVICE_INCLUDE: ("phase" | "resource" | "service-category")[] = [
  "phase",
  "resource",
  "service-category",
];

const relId = (rel: unknown): string | undefined => {
  const id = (rel as { data?: { id?: string | number } } | undefined)?.data?.id;
  return id === undefined || id === null ? undefined : String(id);
};

// A project resource's `resource` attribute carries the account-level link as
// { resource_type: "resources" | "vendors", resource_id }. Catalog services
// point at a standard resource, so match on type "resources" and the id.
const resourceLink = (projectResource: any) =>
  projectResource?.attributes?.resource as
    | { resource_type?: string; resource_id?: number | string }
    | undefined;

export const useAddSuggestedService = (projectId: string) => {
  const dispatch = useDispatch<any>();
  const [createProjectService] = useV2CreateProjectServiceMutation();

  // Reads a query through the store (sharing its cache, e.g. with
  // useServiceValueOptions) without subscribing this component to it.
  const fetchOnce = useCallback(
    async <T>(action: any): Promise<T> => {
      const request = dispatch(action);
      try {
        return (await request.unwrap()) as T;
      } finally {
        request.unsubscribe();
      }
    },
    [dispatch]
  );

  const loadDefaults = useCallback(
    async (taskId: number): Promise<SuggestedServiceDefaults> => {
      // Project phases and resources only refine the defaults; if they fail
      // the user picks those fields. A failed catalog fetch rejects.
      const [serviceResponse, phasesResponse, resourcesResponse] =
        await Promise.all([
          fetchOnce<V2GetServiceByIdApiResponse>(
            generatedV2.endpoints.v2GetServiceById.initiate({
              id: String(taskId),
              include: SERVICE_INCLUDE,
            })
          ),
          fetchOnce<V2ListProjectPhasesForAccountApiResponse>(
            generatedV2.endpoints.v2ListProjectPhasesForAccount.initiate(
              projectPhasesArgs(projectId)
            )
          ).catch(() => undefined),
          fetchOnce<V2ListProjectResourcesForAccountApiResponse>(
            generatedV2.endpoints.v2ListProjectResourcesForAccount.initiate(
              projectResourcesArgs(projectId)
            )
          ).catch(() => undefined),
        ]);

      const service = serviceResponse?.data;
      if (!service) throw new Error("Service not found");
      const attributes =
        service.attributes ?? ({} as typeof service.attributes);
      const relationships = service.relationships ?? ({} as any);
      const values: ServiceValues = {};

      const categoryId = relId(relationships["service-category"]);
      if (categoryId) values.serviceCategory = categoryId;

      const hours = attributes["suggested-hours"];
      if (typeof hours === "number") values.hours = hours;

      values.quantity = Math.max(1, attributes["minimum-quantity"] ?? 1);

      const catalogPhaseId = relId(relationships.phase);
      const phase = catalogPhaseId
        ? (phasesResponse?.data ?? []).find(
            (p) => relId(p.relationships?.phase) === catalogPhaseId
          )
        : undefined;
      if (phase?.id) values.phase = String(phase.id);

      const catalogResourceId = relId(relationships.resource);
      const resource = catalogResourceId
        ? (resourcesResponse?.data ?? []).find((r) => {
            const link = resourceLink(r);
            return (
              link?.resource_type === "resources" &&
              String(link.resource_id) === catalogResourceId
            );
          })
        : undefined;
      // Typed like the resource select's options ("project-resources-2").
      if (resource?.id) values.resource = `project-resources-${resource.id}`;

      return {
        name: attributes.name,
        serviceType: attributes["service-type"] ?? "professional_services",
        values,
      };
    },
    [fetchOnce, projectId]
  );

  const addService = useCallback(
    async (
      taskId: number,
      serviceType: SuggestedServiceType,
      name: string,
      values: ServiceValues
    ): Promise<void> => {
      // Relationship shapes match pages/ProjectServicesV2/Show/Show.tsx.
      const relationships: Record<string, unknown> = {
        project: { data: { id: String(projectId), type: "projects" } },
        // The catalog link makes the API copy the service's subservices.
        service: { data: { id: String(taskId), type: "services" } },
      };
      if (values.resource)
        relationships["project-resource"] = {
          data: { id: values.resource, type: "project-resources" },
        };
      if (values.phase && serviceType === "professional_services")
        relationships["project-phase"] = {
          data: { id: Number(values.phase), type: "project-phases" },
        };
      if (values.serviceCategory)
        relationships["service-category"] = {
          data: {
            id: Number(values.serviceCategory),
            type: "service-categories",
          },
        };

      const data = {
        type: "project-services",
        attributes: {
          name,
          quantity: values.quantity ?? 1,
          "override-hours": values.hours ?? 0,
          "service-type": serviceType,
        },
        relationships,
      } as unknown as V2ProjectServiceResource;

      try {
        await createProjectService({ body: { data } }).unwrap();
      } catch (err) {
        throw new Error(apiErrorMessage(err));
      }
    },
    [createProjectService, projectId]
  );

  return { loadDefaults, addService };
};

export default useAddSuggestedService;
